import { useEffect, useRef, useTransition } from "react";
import useEvent from "react-use-event-hook";

import { RydeUiUnit, SearchQueryForOptimize } from "@ryde/shared";

import { useDynamicFormsFetch } from "./use-dynamic-forms-fetch";

interface IUseInitViewsAndFiltersParams {
  entity: RydeUiUnit;
  searchQueryToOptimize?: SearchQueryForOptimize;
  /**
   * Page readiness on top of the view request IDs — the grid schema, typically, which is fetched by entity
   * name and so can be ready before them. Defaults to ready, for pages that need nothing but the IDs.
   */
  isPageDependenciesReady?: boolean;
  /** Runs before the views request. Open the loading gates the page owns here. */
  onInitStart?: () => void;
  /** Runs once the views and the table filters are set. Open the page's ready gate here. */
  onInitialized?: () => void;
  /**
   * Runs when an init that had already started is invalidated — the IDs or the page dependencies went away
   * again (company switch). Close the ready gate and release every loader `onInitStart` opened here: nothing
   * downstream will do it, because the fetches that normally clear them are gated on that ready gate.
   *
   * It deliberately does not run before the first init, where there is nothing to undo and the page's own
   * gates still hold their initial value.
   */
  onInitSkipped?: () => void;
}

/**
 * Single place that initializes a grid page's views and table filters.
 *
 * Both steps have to wait for `isViewsFetchReady`: `entityId` and `companyId` are resolved by independent
 * login flows that are not awaited before a page mounts, so either can arrive as `undefined` and resolve a
 * moment later. Initializing before both land is not just early, it is wrong — there is no valid views
 * request, and treating that state as an empty list makes `isViewLoadedSF` report the views as loaded, so the
 * filters (and the rows request built from them) ignore the user's default view.
 *
 * The guard lives here instead of in every page hook so that a new page cannot miss it, and so the gates a
 * skipped init leaves behind are released in one place.
 */
export const useInitViewsAndFilters = ({
  entity,
  searchQueryToOptimize,
  isPageDependenciesReady = true,
  onInitStart,
  onInitialized,
  onInitSkipped,
}: IUseInitViewsAndFiltersParams) => {
  const dynamicFormsFetch = useDynamicFormsFetch(entity, searchQueryToOptimize);
  const { fetchAndSetViews, initTableFilters, isViewsFetchReady } =
    dynamicFormsFetch;
  const [isInitPending, startTransition] = useTransition();
  const hasInitStartedRef = useRef(false);

  const runInit = useEvent(async (): Promise<void> => {
    hasInitStartedRef.current = true;
    onInitStart?.();

    // `fetchAndSetViews` reports a failed views request and settles the views state itself, so the init has to
    // finish either way: pages gate their rows fetch on `onInitialized`, so bailing out here would leave them
    // loading with no rows and no way to recover.
    await fetchAndSetViews();

    initTableFilters();
    onInitialized?.();
  });

  const skipInit = useEvent((): void => {
    // Releasing gates before the first init would release loaders this page never opened — the atom behind
    // them is shared, so another page's request could still be using them.
    if (!hasInitStartedRef.current) {
      return;
    }

    hasInitStartedRef.current = false;
    onInitSkipped?.();
  });

  useEffect(() => {
    if (!isViewsFetchReady || !isPageDependenciesReady) {
      skipInit();
      return;
    }

    startTransition(() => {
      void runInit();
    });
  }, [isPageDependenciesReady, isViewsFetchReady, runInit, skipInit]);

  return { ...dynamicFormsFetch, isInitPending };
};
